import { create } from "zustand";
import { send_request } from "../../utils/Request";

function extractApiStatusCode(responseOrError) {
  if (!responseOrError || typeof responseOrError !== "object") {
    return 0;
  }
  return Number(
    responseOrError?.status || responseOrError?.response?.status || 0
  );
}

function extractApiPayload(responseOrError) {
  if (!responseOrError || typeof responseOrError !== "object") {
    return null;
  }
  const rawPayload = responseOrError?.data ?? responseOrError?.response?.data;
  return parseMaybeJsonPayload(rawPayload);
}

function parseMaybeJsonPayload(payload) {
  if (!payload) return null;
  if (typeof payload === "string") {
    try {
      return JSON.parse(payload);
    } catch (_error) {
      return null;
    }
  }
  if (typeof payload === "object") {
    return payload;
  }
  return null;
}

function resolveApiErrorMessage(responseOrError, fallbackMessage) {
  const payload = extractApiPayload(responseOrError);
  if (payload && typeof payload === "object") {
    if (typeof payload.error === "string" && payload.error.trim()) {
      return payload.error.trim();
    }
    if (typeof payload.message === "string" && payload.message.trim()) {
      return payload.message.trim();
    }
  }
  if (
    typeof responseOrError?.message === "string" &&
    responseOrError.message.trim()
  ) {
    return responseOrError.message.trim();
  }
  return fallbackMessage;
}

function buildDynamicFormErrorResponse(responseOrError, fallbackMessage) {
  const statusCode = extractApiStatusCode(responseOrError);
  if (statusCode < 400) {
    return null;
  }
  const payload = extractApiPayload(responseOrError);
  return {
    status: "ERROR",
    httpStatus: statusCode,
    code: typeof payload?.code === "string" ? payload.code : "",
    error: resolveApiErrorMessage(responseOrError, fallbackMessage),
  };
}

function normalizeActiveAccountUsers(users) {
  if (!Array.isArray(users)) return [];
  const seen = new Set();
  return users
    .filter((user) => user && user.roleDisabled !== true)
    .filter((user) => {
      const userName = String(user.userName || "").trim();
      if (!userName || seen.has(userName)) return false;
      seen.add(userName);
      return true;
    })
    .map((user) => ({
      ...user,
      userName: String(user.userName || "").trim(),
      displayName: String(
        user.displayName || user.name || user.fullName || user.userName || ""
      ).trim(),
      email: String(user.email || "").trim(),
    }));
}

const useTaskInboxStore = create((set, get) => ({
  taskInboxRows: [],
  taskInboxFilters: {
    viewPreset: "",
    status: "",
    dueBucket: "",
    approvalRequired: null,
    assignee: "",
    q: "",
  },
  taskInboxView: "list",
  taskInboxCursor: null,
  taskInboxLoading: false,
  taskInboxHasMore: false,
  taskInboxTotal: 0,
  taskInboxSelectedId: null,
  taskInboxSort: { by: "updatedAt", direction: "desc" },
  dynamicFormsById: {},
  sessionForms: [],
  dynamicFormsLoading: false,
  activeAccountUsers: [],
  activeAccountUsersLoading: false,

  fetchActiveAccountUsers: async ({ force = false } = {}) => {
    const state = get();
    if (state.activeAccountUsersLoading) {
      return state.activeAccountUsers;
    }
    if (!force && state.activeAccountUsers.length > 0) {
      return state.activeAccountUsers;
    }

    set({ activeAccountUsersLoading: true });
    try {
      const response = await send_request(
        "authz-service/get-active-users",
        "",
        "",
        "GET"
      );
      const users = normalizeActiveAccountUsers(response?.data);
      set({ activeAccountUsers: users, activeAccountUsersLoading: false });
      return users;
    } catch (e) {
      set({
        error: e.message || "Failed to load active users",
        activeAccountUsers: [],
        activeAccountUsersLoading: false,
      });
      return [];
    }
  },

  fetchSessionForms: async (chatSessionId) => {
    set({ dynamicFormsLoading: true });
    try {
      const response = await send_request(
        `project-service/agent/session/${chatSessionId}/forms`,
        "",
        {},
        "GET"
      );
      const forms = response?.data || [];
      set({
        sessionForms: Array.isArray(forms) ? forms : [],
        dynamicFormsLoading: false,
      });
      return get().sessionForms;
    } catch (e) {
      set({
        error: e.message || "Failed to load forms",
        sessionForms: [],
        dynamicFormsLoading: false,
      });
      return [];
    }
  },

  setTaskInboxView: (view) => {
    const normalized = (view || "").toLowerCase();
    if (!["list", "table", "kanban"].includes(normalized)) return;
    set({ taskInboxView: normalized });
  },

  setTaskInboxSelectedId: (formInstanceId) => {
    set({ taskInboxSelectedId: formInstanceId || null });
  },

  applyTaskInboxFilters: (filters = {}) => {
    set((s) => ({
      taskInboxFilters: { ...s.taskInboxFilters, ...filters },
      taskInboxCursor: null,
    }));
  },

  setTaskInboxSort: (sort = {}) => {
    set((s) => {
      const nextSort = {
        by: sort.by || s.taskInboxSort.by || "priority",
        direction: sort.direction || s.taskInboxSort.direction || "asc",
      };
      return {
        taskInboxSort: nextSort,
        taskInboxRows: get().sortTaskInboxRows([...s.taskInboxRows], nextSort),
      };
    });
  },

  sortTaskInboxRows: (rows = [], sortState) => {
    const effectiveSortState = sortState || get().taskInboxSort;
    const by = effectiveSortState?.by || "priority";
    const direction = effectiveSortState?.direction === "desc" ? "desc" : "asc";
    const dirMultiplier = direction === "desc" ? -1 : 1;

    const valueFor = (row) => {
      if (by === "priority") return Number(row?.priorityRank ?? 99);
      if (by === "updatedAt") return new Date(row?.updatedAt || 0).getTime();
      if (by === "dueAt") return new Date(row?.dueAt || 0).getTime();
      if (by === "title") return String(row?.title || "").toLowerCase();
      return Number(row?.priorityRank ?? 99);
    };

    return [...rows].sort((a, b) => {
      const av = valueFor(a);
      const bv = valueFor(b);
      if (typeof av === "string" || typeof bv === "string") {
        return String(av).localeCompare(String(bv)) * dirMultiplier;
      }
      if (av === bv) {
        const aUpdated = new Date(a?.updatedAt || 0).getTime();
        const bUpdated = new Date(b?.updatedAt || 0).getTime();
        return bUpdated - aUpdated;
      }
      return (av - bv) * dirMultiplier;
    });
  },

  fetchTaskInboxForms: async (overrides = {}, options = {}) => {
    const state = get();
    const append = options?.append === true;
    const force = options?.force === true;
    const limit = options?.limit || 25;
    const nextFilters = { ...state.taskInboxFilters, ...overrides };

    // Skip fetch if we already have data for the same filters (stale-while-revalidate)
    const filtersMatch =
      !force &&
      !append &&
      state.taskInboxRows.length > 0 &&
      JSON.stringify(nextFilters) === JSON.stringify(state.taskInboxFilters);
    if (filtersMatch) {
      // Return cached data immediately, refresh in background
      const bgRefresh = async () => {
        try {
          const bgLimit = Math.max(limit, state.taskInboxRows.length);
          const query = {
            view: nextFilters.viewPreset || "",
            status: nextFilters.status || "",
            dueBucket: nextFilters.dueBucket || "",
            assignee: nextFilters.assignee || "",
            q: nextFilters.q || "",
            limit: bgLimit,
          };
          if (nextFilters.agentId) query.agentId = nextFilters.agentId;
          if (nextFilters.approvalRequired !== null && nextFilters.approvalRequired !== undefined) {
            query.approvalRequired = nextFilters.approvalRequired;
          }
          const response = await send_request("project-service/agent/forms", "", query, "GET");
          const payload = response?.data || {};
          const incomingRows = Array.isArray(payload.rows) ? payload.rows : [];
          const sortedRows = get().sortTaskInboxRows(incomingRows);
          set({
            taskInboxRows: sortedRows,
            taskInboxCursor: payload.nextCursor || null,
            taskInboxHasMore: Boolean(payload.hasMore),
            taskInboxTotal: Number(payload.total || sortedRows.length),
          });
        } catch (_) { /* silent background refresh */ }
      };
      bgRefresh();
      return state.taskInboxRows;
    }

    set({ taskInboxLoading: true });

    try {
      const query = {
        view: nextFilters.viewPreset || "",
        status: nextFilters.status || "",
        dueBucket: nextFilters.dueBucket || "",
        assignee: nextFilters.assignee || "",
        q: nextFilters.q || "",
        limit,
      };
      if (nextFilters.agentId) {
        query.agentId = nextFilters.agentId;
      }
      if (
        nextFilters.approvalRequired !== null &&
        nextFilters.approvalRequired !== undefined
      ) {
        query.approvalRequired = nextFilters.approvalRequired;
      }
      if (append && state.taskInboxCursor) {
        query.cursor = state.taskInboxCursor;
      }

      const response = await send_request(
        "project-service/agent/forms",
        "",
        query,
        "GET"
      );
      const payload = response?.data || {};
      const incomingRows = Array.isArray(payload.rows) ? payload.rows : [];
      const currentRows = get().taskInboxRows;
      const mergedRows = append
        ? [
            ...currentRows,
            ...incomingRows.filter(
              (nextRow) =>
                !currentRows.some(
                  (existing) =>
                    existing.formInstanceId === nextRow.formInstanceId
                )
            ),
          ]
        : incomingRows;
      const sortedRows = get().sortTaskInboxRows(mergedRows);

      set({
        taskInboxRows: sortedRows,
        taskInboxFilters: nextFilters,
        taskInboxCursor: payload.nextCursor || null,
        taskInboxHasMore: Boolean(payload.hasMore),
        taskInboxTotal: Number(payload.total || sortedRows.length),
        taskInboxLoading: false,
      });
      return sortedRows;
    } catch (e) {
      set({
        error: e.message || "Failed to load task inbox",
        taskInboxLoading: false,
      });
      return [];
    }
  },

  refreshTaskInboxAfterMutation: async (formInstanceId) => {
    if (!formInstanceId) return;
    const state = get();
    const rowExists = state.taskInboxRows.some(
      (row) => row.formInstanceId === formInstanceId
    );
    if (rowExists) {
      await get().fetchTaskInboxForms(state.taskInboxFilters);
    }
    if (state.taskInboxSelectedId === formInstanceId) {
      await get().fetchDynamicForm(formInstanceId);
    }
  },

  fetchDynamicForm: async (formInstanceId) => {
    set({ dynamicFormsLoading: true });
    try {
      const response = await send_request(
        `project-service/agent/form/${formInstanceId}`,
        "",
        {},
        "GET"
      );
      const form = response?.data || null;
      if (form && form.formInstanceId) {
        set((s) => ({
          dynamicFormsById: {
            ...s.dynamicFormsById,
            [form.formInstanceId]: form,
          },
          dynamicFormsLoading: false,
        }));
      } else {
        set({ dynamicFormsLoading: false });
      }
      return form;
    } catch (e) {
      set({ error: e.message || "Failed to load form", dynamicFormsLoading: false });
      return null;
    }
  },

  saveDynamicFormDraft: async (
    formInstanceId,
    answers = {},
    idempotencyKey = null
  ) => {
    try {
      const payload = { answers };
      if (idempotencyKey) payload.idempotencyKey = idempotencyKey;
      const response = await send_request(
        `project-service/agent/form/${formInstanceId}/draft`,
        payload,
        {},
        "POST"
      );
      const apiError = buildDynamicFormErrorResponse(
        response,
        "Failed to save form draft"
      );
      if (apiError) {
        set({ error: apiError.error });
        return apiError;
      }
      const data = response?.data || null;
      if (data?.form?.formInstanceId) {
        set((s) => ({
          dynamicFormsById: {
            ...s.dynamicFormsById,
            [data.form.formInstanceId]: data.form,
          },
        }));
      }
      return data;
    } catch (e) {
      set({ error: e.message || "Failed to save form draft" });
      return null;
    }
  },

  submitDynamicForm: async (
    formInstanceId,
    answers = {},
    idempotencyKey = null
  ) => {
    try {
      const payload = { answers };
      if (idempotencyKey) payload.idempotencyKey = idempotencyKey;
      const response = await send_request(
        `project-service/agent/form/${formInstanceId}/submit`,
        payload,
        {},
        "POST"
      );
      const apiError = buildDynamicFormErrorResponse(
        response,
        "Failed to submit form"
      );
      if (apiError) {
        set({ error: apiError.error });
        return apiError;
      }
      const data = response?.data || null;
      if (data?.form?.formInstanceId) {
        set((s) => ({
          dynamicFormsById: {
            ...s.dynamicFormsById,
            [data.form.formInstanceId]: data.form,
          },
        }));
      }
      const shouldRefresh =
        Boolean(data?.form?.formInstanceId) ||
        data?.status === "SUBMITTED" ||
        data?.status === "DUPLICATE_SUBMIT_IGNORED";
      if (shouldRefresh) {
        await get().refreshTaskInboxAfterMutation(formInstanceId);
      }
      return data;
    } catch (e) {
      set({ error: e.message || "Failed to submit form" });
      return null;
    }
  },

  approveDynamicForm: async (formInstanceId, payload = {}) => {
    try {
      const response = await send_request(
        `project-service/agent/form/${formInstanceId}/approve`,
        payload,
        {},
        "POST"
      );
      const data = response?.data || null;
      await get().refreshTaskInboxAfterMutation(formInstanceId);
      return data;
    } catch (e) {
      set({ error: e.message || "Failed to approve form" });
      return null;
    }
  },

  reopenDynamicForm: async (formInstanceId, payload = {}) => {
    try {
      const response = await send_request(
        `project-service/agent/form/${formInstanceId}/reopen`,
        payload,
        {},
        "POST"
      );
      const data = response?.data || null;
      await get().refreshTaskInboxAfterMutation(formInstanceId);
      return data;
    } catch (e) {
      set({ error: e.message || "Failed to reopen form" });
      return null;
    }
  },

  cancelDynamicForm: async (formInstanceId, payload = {}) => {
    try {
      const response = await send_request(
        `project-service/agent/form/${formInstanceId}/cancel`,
        payload,
        {},
        "POST"
      );
      const data = response?.data || null;
      await get().refreshTaskInboxAfterMutation(formInstanceId);
      return data;
    } catch (e) {
      set({ error: e.message || "Failed to cancel form" });
      return null;
    }
  },

  createDynamicFormShareLink: async (formInstanceId, options = {}) => {
    try {
      const response = await send_request(
        `project-service/agent/form/${formInstanceId}/share-links`,
        options,
        {},
        "POST"
      );
      const data = response?.data || null;
      await get().refreshTaskInboxAfterMutation(formInstanceId);
      return data;
    } catch (e) {
      set({ error: e.message || "Failed to create share link" });
      return null;
    }
  },

  revokeDynamicFormShareLink: async (shareId, payload = {}) => {
    try {
      const response = await send_request(
        `project-service/agent/form/share-links/${shareId}/revoke`,
        payload,
        {},
        "POST"
      );
      const data = response?.data || null;
      await get().refreshTaskInboxAfterMutation(
        payload?.formInstanceId ||
          data?.formInstanceId ||
          get().taskInboxSelectedId
      );
      return data;
    } catch (e) {
      set({ error: e.message || "Failed to revoke share link" });
      return null;
    }
  },

  updateDynamicFormSettings: async (formInstanceId, settings = {}) => {
    try {
      const response = await send_request(
        `project-service/agent/form/${formInstanceId}/settings`,
        settings,
        {},
        "POST"
      );
      const apiError = buildDynamicFormErrorResponse(
        response,
        "Failed to update form settings"
      );
      if (apiError) {
        set({ error: apiError.error });
        return apiError;
      }
      const data = response?.data || null;
      if (data?.form?.formInstanceId) {
        set((s) => ({
          dynamicFormsById: {
            ...s.dynamicFormsById,
            [data.form.formInstanceId]: data.form,
          },
        }));
      }
      await get().refreshTaskInboxAfterMutation(formInstanceId);
      return data;
    } catch (e) {
      const apiError = buildDynamicFormErrorResponse(
        e,
        "Failed to update form settings"
      );
      set({
        error:
          apiError?.error || e.message || "Failed to update form settings",
      });
      return apiError || null;
    }
  },

  uploadDynamicFormMedia: async (formInstanceId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await send_request(
        `project-service/agent/form/${formInstanceId}/upload`,
        formData,
        "",
        "POST",
        { "content-type": "multipart/form-data" }
      );
      return response?.data || null;
    } catch (e) {
      set({ error: e.message || "Failed to upload form media" });
      return null;
    }
  },
}));

export default useTaskInboxStore;
